import { useState } from "react"
import { createPortal } from "react-dom"
import { useFirestore, useFirestoreCollectionData } from "reactfire"
import { collection, doc, deleteDoc } from "firebase/firestore"
import { useActividad } from "../hooks/useActividad"
import { UNIDADES, LISTA_UNIDADES } from "../config/unidades"
import InventarioModal from "./InventarioModal"

const ESTADOS = {
  bueno:       { label: "Bueno",       clase: "badge-pagado",    icono: "✅" },
  deteriorado: { label: "Deteriorado", clase: "badge-pendiente", icono: "⚠️" },
  perdido:     { label: "Perdido",     clase: "badge-atrasado",  icono: "❌" },
}

export default function Inventario({ unidadId, puedeEditar, esAdmin }) {
  const firestore = useFirestore()
  const { data: items, status } = useFirestoreCollectionData(
    collection(firestore, "unidades", unidadId, "inventario"), { idField: "id" }
  )

  const { registrar } = useActividad()
  const [busqueda,      setBusqueda]      = useState("")
  const [filtroEstado,  setFiltroEstado]  = useState("todos")
  const [filtroCateg,   setFiltroCateg]   = useState("todas")
  const [modalAbierto,  setModalAbierto]  = useState(false)
  const [itemEditando,  setItemEditando]  = useState(null)
  const [verDetalle,    setVerDetalle]    = useState(null)

  const eliminar = async (item) => {
    if (!window.confirm(`¿Eliminar "${item.nombre}" del inventario?`)) return
    await deleteDoc(doc(firestore, "unidades", unidadId, "inventario", item.id))
    await registrar({ accion: "eliminó", entidad: "inventario", nombre: item.nombre, unidadId })
    if (verDetalle === item.id) setVerDetalle(null)
  }

  if (status === "loading") return <div className="loading">Cargando inventario...</div>

  const lista = items || []

  // Categorías únicas disponibles
  const categorias = [...new Set(lista.map(i => i.categoria).filter(Boolean))].sort()

  // Filtros
  const q = busqueda.trim().toLowerCase()
  const filtrados = lista.filter(item => {
    const coincideBusq   = !q || item.nombre?.toLowerCase().includes(q) || item.descripcion?.toLowerCase().includes(q)
    const coincideEstado = filtroEstado === "todos" || item.estado === filtroEstado
    const coincideCateg  = filtroCateg  === "todas" || item.categoria === filtroCateg
    return coincideBusq && coincideEstado && coincideCateg
  }).sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "", "es"))

  // Totales por estado
  const totalBueno       = lista.filter(i => i.estado === "bueno").length
  const totalDeteriodado = lista.filter(i => i.estado === "deteriorado").length
  const totalPerdido     = lista.filter(i => i.estado === "perdido").length

  const cfgUnidad = UNIDADES[unidadId]

  return (
    <div>
      {/* ── Resumen de estados ── */}
      {lista.length > 0 && (
        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            { label: "Ítems en buen estado", valor: totalBueno,       color: "#dcfce7", texto: "#166534", icono: "✅" },
            { label: "Deteriorados",          valor: totalDeteriodado, color: "#fef9c3", texto: "#854d0e", icono: "⚠️" },
            { label: "Perdidos",              valor: totalPerdido,     color: "#fee2e2", texto: "#991b1b", icono: "❌" },
          ].map(s => (
            <div key={s.label} style={{
              flex: 1, minWidth: 120,
              background: s.color, borderRadius: 12,
              padding: "14px 18px",
            }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.texto }}>{s.icono} {s.valor}</div>
              <div style={{ fontSize: 12, color: s.texto, opacity: 0.8, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
          <div style={{
            flex: 1, minWidth: 120,
            background: "var(--verde-suave)", borderRadius: 12,
            padding: "14px 18px",
          }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "var(--verde)" }}>📦 {lista.length}</div>
            <div style={{ fontSize: 12, color: "var(--verde)", opacity: 0.8, marginTop: 2 }}>Total de ítems</div>
          </div>
        </div>
      )}

      {/* ── Filtros ── */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        {/* Buscador */}
        <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
          <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontSize: 16, pointerEvents: "none", opacity: 0.4 }}>🔍</span>
          <input
            className="form-input"
            style={{ paddingLeft: 40, marginBottom: 0 }}
            placeholder="Buscar ítem por nombre o descripción..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
          />
          {busqueda && (
            <button onClick={() => setBusqueda("")} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "var(--gris)", padding: 4 }}>✕</button>
          )}
        </div>

        {/* Filtro estado */}
        <select
          className="form-select"
          style={{ width: "auto", padding: "8px 14px", fontSize: 13 }}
          value={filtroEstado}
          onChange={e => setFiltroEstado(e.target.value)}
        >
          <option value="todos">Todos los estados</option>
          <option value="bueno">✅ Bueno</option>
          <option value="deteriorado">⚠️ Deteriorado</option>
          <option value="perdido">❌ Perdido</option>
        </select>

        {/* Filtro categoría */}
        {categorias.length > 0 && (
          <select
            className="form-select"
            style={{ width: "auto", padding: "8px 14px", fontSize: 13 }}
            value={filtroCateg}
            onChange={e => setFiltroCateg(e.target.value)}
          >
            <option value="todas">Todas las categorías</option>
            {categorias.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </div>

      {/* Contador */}
      {(q || filtroEstado !== "todos" || filtroCateg !== "todas") && lista.length > 0 && (
        <div style={{ fontSize: 13, color: "var(--gris)", marginBottom: 12 }}>
          {filtrados.length === 0 ? "Sin resultados" : `${filtrados.length} ítem${filtrados.length !== 1 ? "s" : ""} encontrado${filtrados.length !== 1 ? "s" : ""}`}
        </div>
      )}

      {/* ── Lista vacía ── */}
      {lista.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📦</div>
          <div className="empty-text">
            No hay ítems en el inventario.
            {puedeEditar && <><br />Agrega el primero con el botón de arriba.</>}
          </div>
        </div>
      ) : filtrados.length === 0 ? (
        <div className="empty-state" style={{ padding: "40px 20px" }}>
          <div className="empty-icon">🔍</div>
          <div className="empty-text">Sin resultados para ese filtro.<br /><span style={{ fontSize: 13 }}>Intenta con otros criterios.</span></div>
        </div>
      ) : (
        /* ── Grid de tarjetas ── */
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
          {filtrados.map(item => {
            const estadoCfg = ESTADOS[item.estado] || ESTADOS.bueno
            const abierto   = verDetalle === item.id

            return (
              <div
                key={item.id}
                className="card"
                style={{ padding: 0, overflow: "hidden", cursor: "pointer", transition: "box-shadow 0.15s" }}
                onClick={() => setVerDetalle(abierto ? null : item.id)}
              >
                {/* Foto o placeholder */}
                <div style={{
                  height: 140, background: "var(--verde-suave)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  overflow: "hidden", position: "relative",
                }}>
                  {item.fotoUrl
                    ? <img src={item.fotoUrl} alt={item.nombre} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <span style={{ fontSize: 48, opacity: 0.25 }}>📦</span>
                  }
                  {/* Badge de estado sobre la foto */}
                  <span className={`badge ${estadoCfg.clase}`} style={{ position: "absolute", top: 10, right: 10, fontSize: 12 }}>
                    {estadoCfg.icono} {estadoCfg.label}
                  </span>
                </div>

                {/* Info */}
                <div style={{ padding: "14px 16px" }}>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{item.nombre}</div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                    <span style={{ fontSize: 13, color: "var(--gris)" }}>
                      🔢 Cantidad: <strong style={{ color: "var(--texto)" }}>{item.cantidad || 1}</strong>
                    </span>
                    {item.categoria && (
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: "2px 8px",
                        background: "var(--verde-suave)", color: "var(--verde)",
                        borderRadius: 6,
                      }}>
                        {item.categoria}
                      </span>
                    )}
                  </div>

                  {item.ubicacion && (
                    <div style={{ fontSize: 12, color: "var(--gris)", marginBottom: 6 }}>
                      📍 {item.ubicacion}
                    </div>
                  )}

                  {/* Descripción expandible */}
                  {abierto && item.descripcion && (
                    <div style={{
                      fontSize: 13, color: "var(--texto-suave)", fontStyle: "italic",
                      borderTop: "1px dashed var(--gris-borde)", paddingTop: 10, marginTop: 8,
                    }}>
                      💬 {item.descripcion}
                    </div>
                  )}

                  {/* Acciones */}
                  {puedeEditar && (
                    <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}
                      onClick={e => e.stopPropagation()}>
                      <button className="btn-icon" title="Editar" onClick={() => { setItemEditando(item); setModalAbierto(true) }}>✏️</button>
                      <button className="btn-icon" title="Eliminar" onClick={() => eliminar(item)}>🗑️</button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Modal */}
      {modalAbierto && createPortal(
        <InventarioModal
          unidadId={unidadId}
          item={itemEditando}
          onCerrar={() => { setModalAbierto(false); setItemEditando(null) }}
        />,
        document.body
      )}
    </div>
  )
}
